// ============================================================================
// LanguagePicker — floating language switcher for customer views
// ----------------------------------------------------------------------------
// Renders a small pill in the bottom-right of the screen showing the
// customer's currently-selected language alongside the restaurant's default
// (so a quick toggle between the two most-common pair is always one tap
// away). Tapping anywhere on the pill opens a bottom sheet listing every
// translated language for this restaurant, picker-wheel-style. Selecting a
// language closes the sheet and updates the pill.
//
// Props:
//   currentLang   – ISO 639-1 code currently displayed
//   defaultLang   – ISO 639-1 code of the restaurant's default language
//   availableLangs – array of ISO codes the host has translated into
//   onChange(code) – callback when the customer picks a new language
//   accent        – theme color used to highlight the active chip
// ============================================================================
import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Globe, Check } from 'lucide-react';
import { LANGUAGES, isRtl } from '@/utils/i18n';

const codeUpper = (c) => (c || '').toUpperCase();
const nameOf    = (c) => LANGUAGES.find(l => l.code === c) || { code: c, name: c, native: c };

export function LanguagePicker({ currentLang, defaultLang, availableLangs, onChange, accent = '#0f5a78' }) {
  const [open, setOpen] = useState(false);
  // De-duplicated list — always keep the default first, then every other
  // translated language in alphabetical order by native name. Anything
  // outside `availableLangs` is hidden so the picker only ever offers
  // languages the host has actually translated.
  const known = new Set([defaultLang, ...(availableLangs || [])].filter(Boolean));
  const langs = [
    nameOf(defaultLang),
    ...[...known]
      .filter(c => c !== defaultLang)
      .map(nameOf)
      .sort((a, b) => a.native.localeCompare(b.native)),
  ];
  if (langs.length <= 1) return null; // nothing to pick — host hasn't translated yet

  const cur = nameOf(currentLang);
  const def = nameOf(defaultLang);
  const isOnDefault = currentLang === defaultLang;

  return (
    <>
      {/* Bottom-right floating pill. Always renders both the current
          language and the restaurant default so the customer can quickly
          flip back to source. Tapping anywhere opens the full picker. */}
      <button
        type="button"
        data-testid="lang-pill"
        onClick={() => setOpen(true)}
        dir="ltr"
        className="fixed bottom-4 right-4 z-50 flex items-center gap-1 rounded-full bg-white/95 backdrop-blur shadow-lg border border-stone-200 p-0.5 active:scale-95 transition-transform"
        aria-label="Change language"
      >
        <span
          className="min-w-[36px] h-8 px-2.5 rounded-full text-xs font-extrabold uppercase tracking-wider flex items-center justify-center"
          style={{ backgroundColor: accent, color: '#fff' }}
        >{codeUpper(cur.code)}</span>
        {!isOnDefault && (
          <span className="min-w-[36px] h-8 px-2.5 rounded-full text-xs font-extrabold uppercase tracking-wider text-stone-500 flex items-center justify-center">
            {codeUpper(def.code)}
          </span>
        )}
        <span className="h-8 w-8 rounded-full flex items-center justify-center text-stone-500">
          <Globe size={16} />
        </span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          // 60vh on phones gives the iOS-style wheel feel — enough rows
          // to scroll but doesn't take over the whole screen. Always LTR
          // even when current lang is RTL so the picker layout stays
          // predictable across all 20 languages.
          className="h-[60vh] flex flex-col rounded-t-3xl p-4"
          data-testid="lang-picker-sheet"
          dir="ltr"
        >
          <SheetHeader className="shrink-0">
            <SheetTitle className="flex items-center gap-2 text-base font-bold" style={{ fontFamily: 'Outfit' }}>
              <Globe size={18} /> Choose your language
            </SheetTitle>
          </SheetHeader>
          {/* Picker list — one tap fires onChange + closes the sheet.
              Active language highlighted with the restaurant's accent. */}
          <div className="mt-2 flex-1 overflow-y-auto min-h-0 divide-y divide-stone-100" data-testid="lang-picker-list">
            {langs.map(l => {
              const active = l.code === currentLang;
              return (
                <button
                  key={l.code}
                  type="button"
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => {
                    onChange?.(l.code);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between gap-3 py-3 px-2 text-left ${
                    active ? 'bg-stone-50' : 'hover:bg-stone-50'
                  }`}
                  dir={isRtl(l.code) ? 'rtl' : 'ltr'}
                >
                  <div className="min-w-0">
                    <p className="font-bold text-stone-900 truncate" style={{ fontFamily: 'Outfit' }}>{l.native}</p>
                    <p className="text-xs text-stone-400 truncate">{l.name}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={active
                        ? { backgroundColor: accent, color: '#fff' }
                        : { backgroundColor: '#f5f5f4', color: '#78716c' }}
                    >{codeUpper(l.code)}</span>
                    {active && <Check size={16} style={{ color: accent }} />}
                  </div>
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// Helper: detect a sensible initial language. Order of precedence:
//   1. The customer's persisted choice in localStorage (if still valid)
//   2. navigator.language base code (e.g. "fr-CA" → "fr"), if the host has
//      actually translated into it
//   3. The restaurant's default language
//
// Returns the chosen code so callers can `setLangState(detectInitialLang(...))`.
export function detectInitialLang({ restaurantId, defaultLang, availableLangs }) {
  const known = new Set([defaultLang, ...(availableLangs || [])].filter(Boolean));
  try {
    const saved = localStorage.getItem(`cust_lang:${restaurantId}`);
    if (saved && known.has(saved)) return saved;
  } catch { /* ignore */ }
  try {
    const nav = (navigator.language || '').split('-')[0].toLowerCase();
    if (nav && known.has(nav)) return nav;
  } catch { /* ignore */ }
  return defaultLang || 'en';
}
